import { useEffect, useState } from "react";
import axios from "axios";
import { API, BACKEND_URL } from "@/lib/api";

export default function BrandingProvider({ children }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchBrandingData = async () => {
      try {
        // Fetch all branding data in parallel
        const responses = await Promise.all([
          axios.get(`${API}/branding/colors`).catch(() => ({ data: {} })),
          axios.get(`${API}/branding/logo-scale`).catch(() => ({ data: { scale: 100 } })),
          axios.get(`${API}/branding/favicon`).catch(() => ({ data: {} }))
        ]);

        const colors = (responses[0].data && typeof responses[0].data === 'object') ? responses[0].data : {};
        const logoScale = (responses[1].data && typeof responses[1].data === 'object') ? (responses[1].data.scale || 100) : 100;
        const faviconData = (responses[2].data && typeof responses[2].data === 'object') ? responses[2].data : {};

        const root = document.documentElement;

        // Apply colors as CSS variables
        if (colors.primary_color && typeof colors.primary_color === 'string' && colors.primary_color.startsWith('#')) {
          root.style.setProperty('--brand-primary', colors.primary_color);
          const hsl = hexToHSL(colors.primary_color);
          if (hsl) {
            root.style.setProperty('--brand-primary-hsl', hsl);
            root.style.setProperty('--primary', hsl);
          }
        }

        if (colors.accent_color && typeof colors.accent_color === 'string' && colors.accent_color.startsWith('#')) {
          root.style.setProperty('--brand-accent', colors.accent_color);
          const hsl = hexToHSL(colors.accent_color);
          if (hsl) {
            root.style.setProperty('--brand-accent-hsl', hsl);
            root.style.setProperty('--accent', hsl);
          }
        }

        if (colors.text_color && typeof colors.text_color === 'string' && colors.text_color.startsWith('#')) {
          root.style.setProperty('--brand-text', colors.text_color);
          const hsl = hexToHSL(colors.text_color);
          if (hsl) {
            root.style.setProperty('--brand-text-hsl', hsl);
            root.style.setProperty('--foreground', hsl);
          }
        }

        if (colors.background_color && typeof colors.background_color === 'string' && colors.background_color.startsWith('#')) {
          root.style.setProperty('--brand-background', colors.background_color);
          const hsl = hexToHSL(colors.background_color);
          if (hsl) {
            root.style.setProperty('--brand-background-hsl', hsl);
            root.style.setProperty('--background', hsl);
          }
        }

        // Apply logo scale as CSS variable
        root.style.setProperty('--logo-scale', `${logoScale / 100}`);

        // Apply favicon if available
        if (faviconData.favicon_url) {
          const faviconUrl = `${BACKEND_URL}${faviconData.favicon_url}`;

          // Update or create favicon link
          let link = document.querySelector("link[rel*='icon']");
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.type = 'image/x-icon';
          link.href = faviconUrl;
        }

      } catch (error) {
        console.error("Failed to fetch branding data:", error);
      } finally {
        setLoaded(true);
      }
    };

    fetchBrandingData();
  }, []);

  return children;
}

// Helper function to convert hex to HSL string (for Tailwind CSS variables)
function hexToHSL(hex) {
  if (!hex || typeof hex !== 'string') return null;
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Parse hex
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;

  let max = Math.max(r, g, b);
  let min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
      default:
        h = 0;
    }
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return `${h} ${s}% ${l}%`;
}
